import { forwardRef, useState } from "react";

/**
 * <GlassButton/> — smoked-glass pill with a MASKED chrome-gradient perimeter.
 *
 *   Layered materials (bottom → top):
 *     0. Translucent smoked-black fill + backdrop-filter blur+saturate
 *     1. Content span
 *     2. Inner reflection — thin upper-edge white band, subtle diagonal
 *     3. Chrome (or gold) frame ring — masked gradient, visible at REST
 *     4. Top hotspot — narrow bright reflection above the frame
 *
 * Rest is intentionally visible (not just a hover-only affordance). Chrome
 * traces polished-metal highlights; gold uses champagne→honey→bronze so the
 * active state reads as illuminated metal rather than flat yellow.
 *
 * Tones: chrome (default) | gold (active/selected) | ghost (minimal).
 */

const CHROME_REST  = "linear-gradient(135deg, rgba(255,255,255,0.65) 0%, rgba(160,168,180,0.35) 22%, rgba(255,255,255,0.55) 42%, rgba(60,66,76,0.5) 60%, rgba(220,225,235,0.58) 80%, rgba(90,96,106,0.4) 100%)";
const CHROME_HOVER = "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(190,198,210,0.55) 20%, rgba(255,255,255,0.78) 40%, rgba(80,86,96,0.7) 60%, rgba(240,244,252,0.9) 80%, rgba(120,126,136,0.55) 100%)";
const GOLD_REST    = "linear-gradient(135deg, rgba(255,240,190,0.95) 0%, rgba(220,170,55,0.85) 22%, rgba(254,215,110,0.9) 42%, rgba(120,75,15,0.8) 60%, rgba(255,225,150,0.9) 80%, rgba(150,90,25,0.75) 100%)";

// The mask stack that turns padding into a border ring.
const RING_MASK = {
  WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
  WebkitMaskComposite: "xor",
  mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
  maskComposite: "exclude",
};

const TONE_META = {
  chrome: {
    bg:
      "linear-gradient(180deg, rgba(30,30,34,0.62) 0%, rgba(10,10,12,0.78) 55%, rgba(4,4,5,0.82) 100%)",
    color: "text-zinc-100",
    frameRest: CHROME_REST,
    frameHover: CHROME_HOVER,
    edgeRest: "linear-gradient(90deg, transparent 5%, rgba(255,255,255,0.55) 50%, transparent 95%)",
    edgeHover: "linear-gradient(90deg, transparent 5%, rgba(255,255,255,0.85) 50%, transparent 95%)",
    outerRest: "0 3px 10px -6px rgba(0,0,0,0.7)",
    outerHover: [
      "0 0 0 1px rgba(255,255,255,0.14)",
      "0 8px 20px -6px rgba(255,255,255,0.18)",
      "0 3px 12px -6px rgba(0,0,0,0.7)",
    ].join(", "),
    outerPress: "0 2px 6px -4px rgba(0,0,0,0.7)",
  },
  gold: {
    bg:
      "linear-gradient(180deg, rgba(60,44,15,0.72) 0%, rgba(30,20,4,0.82) 55%, rgba(15,10,2,0.88) 100%)",
    color: "text-amber-100",
    frameRest: GOLD_REST,
    frameHover: GOLD_REST,   // gold stays warm; hover strengthens the outer halo
    edgeRest: "linear-gradient(90deg, transparent 5%, rgba(255,240,190,0.85) 50%, transparent 95%)",
    edgeHover: "linear-gradient(90deg, transparent 5%, rgba(255,245,210,1) 50%, transparent 95%)",
    outerRest: [
      "0 0 0 1px rgba(220,170,55,0.35)",
      "0 6px 20px -8px rgba(220,170,55,0.5)",
    ].join(", "),
    outerHover: [
      "0 0 0 1px rgba(220,170,55,0.55)",
      "0 0 22px -4px rgba(220,170,55,0.55)",
      "0 10px 28px -8px rgba(220,170,55,0.4)",
    ].join(", "),
    outerPress: [
      "0 0 0 1px rgba(220,170,55,0.4)",
      "0 3px 10px -6px rgba(120,75,15,0.6)",
    ].join(", "),
  },
  ghost: {
    bg: "transparent",
    color: "text-zinc-400",
    frameRest: "linear-gradient(135deg, rgba(255,255,255,0.15), rgba(255,255,255,0.06))",
    frameHover: "linear-gradient(135deg, rgba(255,255,255,0.3), rgba(255,255,255,0.12))",
    edgeRest: "transparent",
    edgeHover: "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
    outerRest: "none",
    outerHover: "0 4px 14px -8px rgba(0,0,0,0.7)",
    outerPress: "none",
  },
};

const SIZE = {
  sm: { cls: "h-8 px-3.5 text-[11px]", pad: "1px" },
  md: { cls: "h-10 px-4 text-xs", pad: "1.5px" },
  lg: { cls: "h-11 px-5 text-sm", pad: "1.5px" },
};

const GlassButton = forwardRef(function GlassButton(
  { tone = "chrome", size = "md", className = "", style, children, disabled, ...rest },
  ref,
) {
  const t = TONE_META[tone] || TONE_META.chrome;
  const s = SIZE[size] || SIZE.md;
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);

  const outerShadow = pressed ? t.outerPress : hover ? t.outerHover : t.outerRest;
  const frameGrad = hover ? t.frameHover : t.frameRest;
  const edgeGrad = hover ? t.edgeHover : t.edgeRest;

  return (
    <button
      ref={ref}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onBlur={() => setPressed(false)}
      className={[
        "group relative inline-flex items-center justify-center gap-1.5 rounded-full font-semibold whitespace-nowrap overflow-visible",
        "transition-[box-shadow,transform] duration-[180ms] ease-out",
        "motion-reduce:transition-none motion-reduce:transform-none",
        "motion-safe:hover:-translate-y-[0.5px]",
        "active:translate-y-[0px]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/60",
        "disabled:opacity-45 disabled:pointer-events-none",
        s.cls,
        t.color,
        className,
      ].join(" ")}
      style={{
        background: t.bg,
        backdropFilter: tone === "ghost" ? undefined : "blur(14px) saturate(140%)",
        WebkitBackdropFilter: tone === "ghost" ? undefined : "blur(14px) saturate(140%)",
        boxShadow: outerShadow,
        ...style,
      }}
      {...rest}
    >
      {/* Inner reflection — subtle upper glass sheen */}
      {tone !== "ghost" && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-[1.5px] rounded-full"
          style={{
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.03) 12%, transparent 40%)",
          }}
        />
      )}

      {/* Chrome / gold frame ring — MASKED gradient border */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-full"
        style={{
          padding: s.pad,
          background: frameGrad,
          ...RING_MASK,
          transition: "background 180ms ease-out",
        }}
      />

      {/* Top edge hotspot */}
      {tone !== "ghost" && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-2 top-[1.5px] h-[1px] rounded-full"
          style={{
            background: edgeGrad,
            opacity: hover || tone === "gold" ? 1 : 0.75,
            transition: "opacity 180ms ease-out, background 180ms ease-out",
          }}
        />
      )}

      <span className="relative z-[1] inline-flex items-center gap-1.5">{children}</span>
    </button>
  );
});

export default GlassButton;
